/**
 * 在庫調整モーダル(増減統合)
 *
 * v1.6: 旧「在庫調整(増)」モーダルから、減側の `ADJUSTMENT_OUT`(調整(減))も
 * 扱えるよう統合。選択肢ラベルに `(増)` / `(減)` を明示し、選択直後に
 * 在庫変動方向を Tag で視覚化する。
 *
 * - 増減の振り分けは reasonCode 由来(`getAdjustmentDirection`)。
 * - 注意 Alert「※新規購入による入庫は『発注管理』から実施してください」は維持。
 * - サーバー API は据え置き(`recordInventoryAdjustment` / `recordConsumption` を
 *   呼出側 `InventoryPage` で reasonCode に応じて振り分ける)。
 */
import { useState } from 'react';
import { Alert, Button, Form, Input, InputNumber, Modal, Select, Space, Tag, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  ADJUSTMENT_REASON_OPTIONS,
  getAdjustmentDirection,
  type AdjustmentReasonCode,
  type AdjustmentDirection,
} from '../types/inventory';
import { useIsFormSubmittable } from '@/shared/hooks/useIsFormSubmittable';

const { TextArea } = Input;

const QUANTITY_INPUT_MIN = 1;
const NOTE_TEXTAREA_ROWS = 3;

/** 在庫調整フォームの入力値 */
export interface InventoryAdjustmentFormValues {
  readonly quantity: number;
  readonly reasonCode: AdjustmentReasonCode;
  readonly note?: string;
}

interface Props {
  readonly open: boolean;
  readonly title: string;
  readonly isSubmitting: boolean;
  readonly onSubmit: (values: InventoryAdjustmentFormValues) => Promise<void>;
  readonly onCancel: () => void;
}

/** 増減方向に応じた Tag の色とラベル */
function describeDirection(params: {
  readonly direction: AdjustmentDirection;
}): { readonly color: string; readonly label: string } {
  if (params.direction === 'increase') {
    return { color: 'green', label: '在庫が増えます' };
  }
  return { color: 'volcano', label: '在庫が減ります' };
}

export function InventoryAdjustmentModal({
  open,
  title,
  isSubmitting,
  onSubmit,
  onCancel,
}: Props): React.ReactElement {
  const [form] = Form.useForm<InventoryAdjustmentFormValues>();
  const isSubmittable = useIsFormSubmittable({ form, isFormVisible: open });
  const [selectedReasonCode, setSelectedReasonCode] = useState<AdjustmentReasonCode | null>(null);

  const handleReasonChange = (value: AdjustmentReasonCode): void => {
    setSelectedReasonCode(value);
  };

  const handleFinish = async (values: InventoryAdjustmentFormValues): Promise<void> => {
    await onSubmit(values);
  };

  const directionPreview =
    selectedReasonCode !== null
      ? describeDirection({
          direction: getAdjustmentDirection({ reasonCode: selectedReasonCode }),
        })
      : null;

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      footer={null}
      destroyOnHidden
      afterClose={() => setSelectedReasonCode(null)}
    >
      <Alert
        message="※新規購入による入庫は『発注管理』から実施してください"
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          name="quantity"
          label="数量"
          rules={[
            { required: true, message: '数量を入力してください' },
            { type: 'integer', min: QUANTITY_INPUT_MIN, message: '1以上の整数を入力してください' },
          ]}
        >
          <InputNumber className="w-full" placeholder="数量を入力" />
        </Form.Item>
        <Form.Item
          name="reasonCode"
          label="理由"
          rules={[{ required: true, message: '理由を選択してください' }]}
        >
          <Select placeholder="調整理由を選択" onChange={handleReasonChange}>
            {ADJUSTMENT_REASON_OPTIONS.map((option) => (
              <Select.Option key={option.value} value={option.value}>
                <Space size={4}>
                  {option.label}
                  <Tooltip title={option.tooltip}>
                    <QuestionCircleOutlined />
                  </Tooltip>
                </Space>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        {directionPreview !== null && (
          <Form.Item>
            <Tag color={directionPreview.color}>{directionPreview.label}</Tag>
          </Form.Item>
        )}
        <Form.Item name="note" label="特記事項">
          <TextArea rows={NOTE_TEXTAREA_ROWS} placeholder="特記事項があれば入力" />
        </Form.Item>
        <Form.Item className="mb-0 text-right">
          <Space>
            <Button onClick={onCancel}>キャンセル</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
              disabled={!isSubmittable}
            >
              在庫調整を確定
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
}
